import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Extension } from '@tiptap/core';
import { createLowlight } from 'lowlight';
import {
  Bold,
  Italic,
  Code,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Undo,
  Redo,
  ImageIcon,
  ChevronDown,
  Link as LinkIcon
} from 'lucide-react';

// Import enhanced syntax highlighting styles
import '../../styles/syntax-highlighting.css';

// Create lowlight instance
const lowlight = createLowlight();

// Import common languages for syntax highlighting
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import python from 'highlight.js/lib/languages/python';
import css from 'highlight.js/lib/languages/css';
import xml from 'highlight.js/lib/languages/xml';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import c from 'highlight.js/lib/languages/c';
import cpp from 'highlight.js/lib/languages/cpp';
import x86asm from 'highlight.js/lib/languages/x86asm';


// Register languages with lowlight
lowlight.register('javascript', javascript);
lowlight.register('typescript', typescript);
lowlight.register('python', python);
lowlight.register('css', css);
lowlight.register('html', xml);
lowlight.register('xml', xml);
lowlight.register('json', json);
lowlight.register('bash', bash);
lowlight.register('c', c);
lowlight.register('cpp', cpp);
lowlight.register('c++', cpp);
lowlight.register('asm', x86asm);
lowlight.register('assembly', x86asm);

const TiptapEditor = ({ content, onChange }) => {
  const [selectedLanguage, setSelectedLanguage] = useState('cpp');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Available languages for code blocks
  const availableLanguages = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'python', label: 'Python' },
    { value: 'cpp', label: 'C++' },
    { value: 'c', label: 'C' },
    { value: 'css', label: 'CSS' },
    { value: 'html', label: 'HTML' },
    { value: 'json', label: 'JSON' },
    { value: 'bash', label: 'Bash' },
    { value: 'asm', label: 'Assembly' }
  ];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // Disable default code block to use our enhanced one
      }),
      CodeBlockLowlight.configure({
        lowlight,
        defaultLanguage: 'cpp',
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      Link.configure({
        openOnClick: false, // Prevent accidental navigation in editor
        HTMLAttributes: {
          class: 'text-blue-400 hover:text-blue-300 transition-colors cursor-pointer',
          rel: 'noopener noreferrer',
        },
        validate: href => /^https?:\/\//.test(href), // Basic URL validation
      }),
      // Custom keyboard shortcuts extension
      Extension.create({
        name: 'customKeyboardShortcuts',
        addKeyboardShortcuts() {
          return {
            // CTRL + I for inserting images
            'Mod-i': () => {
              insertImage();
              return true;
            },
            // CTRL + L for links
            'Mod-l': () => {
              toggleLink();
              return true;
            },
            // CTRL + E for code blocks (inserts empty code block)
            'Mod-e': () => {
              insertEmptyCodeBlock();
              return true;
            },
            // CTRL + U for inline code
            'Mod-u': () => {
              editor.chain().focus().toggleCode().run();
              return true;
            },
          };
        },
      }),
    ],
    content: content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[300px] p-4 bg-slate-800 rounded-lg border border-slate-600 text-slate-200',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
  });

  // Helper functions
  const insertImage = () => {
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);
    
    const url = window.prompt('Enter image URL:');
    if (url) {
      const imageAttrs = { src: url };
      
      // If text is selected, use it as alt text
      if (selectedText && selectedText.trim()) {
        imageAttrs.alt = selectedText.trim();
      }
      
      editor.chain().focus().setImage(imageAttrs).run();
    }
  };

  // Link management functions
  const insertLink = () => {
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);
    
    if (selectedText) {
      // Text is selected - just ask for URL
      const url = window.prompt('Enter URL:', 'https://');
      if (url && url.trim()) {
        editor.chain().focus().setLink({ href: url.trim() }).run();
      }
    } else {
      // No selection - ask for both URL and text
      const url = window.prompt('Enter URL:', 'https://');
      if (url && url.trim()) {
        const text = window.prompt('Enter link text:');
        if (text && text.trim()) {
          editor.chain().focus().insertContent(`<a href="${url.trim()}">${text.trim()}</a>`).run();
        }
      }
    }
  };

  const toggleLink = () => {
    if (editor.isActive('link')) {
      // Edit existing link
      const currentUrl = editor.getAttributes('link').href;
      const newUrl = window.prompt('Edit URL:', currentUrl);
      if (newUrl === null) return; // User cancelled
      if (newUrl === '') {
        editor.chain().focus().unsetLink().run(); // Remove link
      } else {
        editor.chain().focus().setLink({ href: newUrl.trim() }).run(); // Update link
      }
    } else {
      insertLink();
    }
  };

  const toggleCodeBlockWithLanguage = (language = selectedLanguage) => {
    if (editor.isActive('codeBlock')) {
      // If already in a code block, change the language
      editor.chain().updateAttributes('codeBlock', { language }).run();
    } else {
      // Create new code block with selected language
      editor.chain().toggleCodeBlock({ language }).run();
    }
  };

  const insertEmptyCodeBlock = (language = selectedLanguage) => {
    // Insert a fresh, empty code block without converting existing content
    editor.chain().focus().insertContent(`<pre><code class="language-${language}"></code></pre>`).run();
  };

  const getCurrentCodeBlockLanguage = () => {
    if (editor.isActive('codeBlock')) {
      const attrs = editor.getAttributes('codeBlock');
      return attrs.language || 'cpp';
    }
    return selectedLanguage;
  };

  // Update editor content when prop changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  // Update selected language when cursor moves to different code block
  useEffect(() => {
    if (editor) {
      const updateLanguage = () => {
        if (editor.isActive('codeBlock')) {
          const currentLang = getCurrentCodeBlockLanguage();
          setSelectedLanguage(currentLang);
        }
      };

      editor.on('selectionUpdate', updateLanguage);
      return () => {
        editor.off('selectionUpdate', updateLanguage);
      };
    }
  }, [editor]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLanguageDropdown && !event.target.closest('.relative')) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageDropdown]);

  if (!editor) {
    return null;
  }

  const ToolbarButton = ({ onClick, isActive, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      className={`p-2 rounded transition-colors ${
        isActive 
          ? 'bg-indigo-600 text-white' 
          : 'text-slate-400 hover:text-white hover:bg-slate-700'
      }`}
      title={title}
    >
      {children}
    </button>
  );

  return (
    <div className="tiptap-editor border border-slate-600 rounded-lg bg-slate-800">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-3 border-b border-slate-600 bg-slate-750">
        <div className="flex items-center gap-1 mr-4">
          <ToolbarButton
            onClick={() => editor.chain().undo().run()}
            title="Undo"
          >
            <Undo size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().redo().run()}
            title="Redo"
          >
            <Redo size={16} />
          </ToolbarButton>
        </div>

        <div className="flex items-center gap-1 mr-4">
          <ToolbarButton
            onClick={() => editor.chain().toggleHeading({ level: 1 }).run()}
            isActive={editor.isActive('heading', { level: 1 })}
            title="Heading 1"
          >
            <Heading1 size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive('heading', { level: 2 })}
            title="Heading 2"
          >
            <Heading2 size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().toggleHeading({ level: 3 }).run()}
            isActive={editor.isActive('heading', { level: 3 })}
            title="Heading 3"
          >
            <Heading3 size={16} />
          </ToolbarButton>
        </div>

        <div className="flex items-center gap-1 mr-4">
          <ToolbarButton
            onClick={() => editor.chain().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title="Bold"
          >
            <Bold size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title="Italic"
          >
            <Italic size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().toggleCode().run()}
            isActive={editor.isActive('code')}
            title="Inline Code"
          >
            <Code size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleLink}
            isActive={editor.isActive('link')}
            title="Insert/Edit Link"
          >
            <LinkIcon size={16} />
          </ToolbarButton>
        </div>

        <div className="flex items-center gap-1 mr-4">
          <ToolbarButton
            onClick={() => editor.chain().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <List size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title="Numbered List"
          >
            <ListOrdered size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            title="Quote"
          >
            <Quote size={16} />
          </ToolbarButton>
        </div>

        <div className="flex items-center gap-1">
          {/* Language Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              className={`flex items-center gap-1 px-2 py-2 rounded text-xs transition-colors ${
                editor.isActive('codeBlock')
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
              title="Select Code Language"
            >
              <span>{availableLanguages.find(lang => lang.value === getCurrentCodeBlockLanguage())?.label || 'C++'}</span>
              <ChevronDown size={12} />
            </button>
            
            {showLanguageDropdown && (
              <div className="absolute top-full left-0 mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg z-10 min-w-[120px]">
                {availableLanguages.map((language) => (
                  <button
                    key={language.value}
                    type="button"
                    onClick={() => {
                      setSelectedLanguage(language.value);
                      toggleCodeBlockWithLanguage(language.value);
                      setShowLanguageDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-600 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                      getCurrentCodeBlockLanguage() === language.value
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-300'
                    }`}
                  >
                    {language.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Code Block Button */}
          <ToolbarButton
            onClick={() => toggleCodeBlockWithLanguage()}
            isActive={editor.isActive('codeBlock')}
            title="Code Block"
          >
            <div className="flex items-center gap-1">
              <Code size={16} />
              <span className="text-xs">Block</span>
            </div>
          </ToolbarButton>

          {/* Image Button */}
          <ToolbarButton
            onClick={insertImage}
            isActive={false}
            title="Insert Image"
          >
            <ImageIcon size={16} />
          </ToolbarButton>
        </div>
      </div>

      {/* Editor Content */}
      <div className="min-h-[300px]">
        <EditorContent editor={editor} />
      </div>

      {/* Help Text */}
      <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-600 bg-slate-750">
        <div className="flex flex-wrap gap-4">
          <span># ## ### for headings</span>
          <span>**bold** or Ctrl+B</span>
          <span>*italic*</span>
          <span>`inline code` or Ctrl+U</span>
          <span>``` for code blocks or Ctrl+E</span>
          <span>Ctrl+I for images</span>
          <span>Ctrl+L for links</span>
        </div>
      </div>
    </div>
  );
};

export default TiptapEditor;